import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, FilePlus, Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDocuments } from '../../context/DocumentsContext';
import { useTheme } from '../../context/ThemeContext';

type NavItem = {
  title: string;
  path: string;
  icon: React.ReactNode;
};

const navItems: NavItem[] = [
  {
    title: 'Tableau de bord',
    path: '/app',
    icon: <LayoutDashboard className="h-5 w-5" />,
  },
  {
    title: 'Clients',
    path: '/app/clients',
    icon: <Users className="h-5 w-5" />,
  },
  {
    title: 'Nouveau Document',
    path: '/app/create',
    icon: <FilePlus className="h-5 w-5" />,
  },
  {
    title: 'Documents',
    path: '/app/documents',
    icon: <FileText className="h-5 w-5" />,
  },
];

const Sidebar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const { documents } = useDocuments();
  const { theme } = useTheme();
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  // Get documents created since the last subscription renewal
  const getDocumentsSinceRenewal = () => {
    if (!user?.subscription_period_start) return [];
    
    const renewalStart = new Date(user.subscription_period_start * 1000);
    return documents.filter(doc => {
      const docDate = new Date(doc.created_at || '');
      return docDate >= renewalStart;
    });
  };

  // Get documents count since last renewal
  const documentsCreated = getDocumentsSinceRenewal().length;

  // Determine if user has Premium plan based on subscription price_id
  const isPremium = user?.subscription_tier === 'PREMIUM';

  // Determine document limit based on subscription
  const documentLimit = isPremium ? Infinity : 40;
  const documentsRemaining = isPremium ? 'Illimité' : Math.max(0, documentLimit - documentsCreated);
  const usagePercentage = isPremium ? 100 : Math.min(100, (documentsCreated / documentLimit) * 100);
  
  return (
    <>
      <button
        className="md:hidden fixed top-4 left-4 z-20 p-2 rounded-md bg-white dark:bg-gray-800 shadow-md"
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? (
          <X className="h-6 w-6 text-gray-700 dark:text-gray-300" />
        ) : (
          <Menu className="h-6 w-6 text-gray-700 dark:text-gray-300" />
        )}
      </button>
      
      <aside 
        className={`
          bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700
          w-64 transition-all duration-300 ease-in-out z-10
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
          md:translate-x-0 fixed md:relative h-full
        `}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-center h-16 px-6 border-b border-gray-200 dark:border-gray-700">
            <Link to={user ? '/app' : '/'}>
              <img 
                src={theme === 'dark' ? '/logoblanc.png' : '/logo.png'} 
                alt="Invoice AI Logo" 
                className="h-8 w-auto"
              />
            </Link>
          </div>
          
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `
                      flex items-center px-4 py-3 rounded-md text-sm font-medium transition-colors
                      ${isActive 
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' 
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/30'}
                    `}
                    onClick={() => setIsMobileMenuOpen(false)}
                    end={item.path === '/app'}
                  >
                    {item.icon}
                    <span className="ml-3">{item.title}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
          
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="py-3 px-4 rounded-md bg-primary-50 dark:bg-primary-900/20">
              <p className="text-xs font-medium text-primary-700 dark:text-primary-300">
                Plan {isPremium ? 'Premium' : 'Essentiel'}
              </p>
              <div className="mt-1 w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="bg-primary-500 h-full rounded-full" 
                  style={{ width: `${usagePercentage}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                {documentsCreated}/{isPremium ? '∞' : documentLimit} documents créés
              </p>
              {user?.subscription_period_end && (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  Renouvellement le {new Date(user.subscription_period_end * 1000).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </aside>
      
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-0 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;